import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getReactionButtons, getReactionLabel } from '../utils/reactionLabels';

/**
 * Premium fullscreen image viewer.
 *
 * Mobile improvements (this revision):
 *   • Arrow buttons are smaller (36px), edge-aligned (10px), slightly more
 *     translucent on mobile, and never cover the image center.
 *   • Inline reactions live in a calm floating toolbar below the image so
 *     the client can react without leaving fullscreen. State stays in
 *     sync with the grid via the `onReact` parent callback.
 *   • Thumbnail strip is smaller (40px) with tighter gaps and smooth
 *     horizontal scroll. Active thumb auto-scrolls into view as the user
 *     navigates with arrows / swipes / keyboard.
 *   • Keyboard hint is hidden on touch devices (it's just visual noise on
 *     phones) and the image gets a touch more breathing room.
 *
 * Desktop intentionally unchanged — only the inline reaction toolbar is
 * added on desktop too so the cross-surface sync feels natural.
 */
const ImageLightbox = ({
  images,
  initialIndex = 0,
  onClose,
  // Optional — when present, the client can react inside the lightbox.
  // Signature: onReact(image, reactionValue) where reactionValue is one
  // of 'love' | 'neutral' | 'not_me'. The same handler used by the grid
  // view, so the state stays naturally synced.
  onReact = null,
  reactionMode = 'mood',
  // When the parent owns a "selected participant" (shared-decision mode),
  // resolve the current reaction from `image.reactions[pid]` instead of
  // `image.reaction`. Returning null means "no current reaction".
  currentReactionFor = null,
  // Optional translation map for accessibility labels — fall back to EN.
  t = {},
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const stripRef = useRef(null);

  const currentImage = images[currentIndex];

  // Resolve the active reaction value: prefer the parent's resolver,
  // otherwise read off the image directly. This keeps the toolbar's
  // "active" highlight identical to the grid view at all times.
  const activeReaction = (() => {
    if (!currentImage) return null;
    if (typeof currentReactionFor === 'function') {
      return currentReactionFor(currentImage);
    }
    return currentImage.reaction || null;
  })();

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') goToNext();
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') goToPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, goToNext, goToPrev]);

  // Keep the active thumb visible when the index changes (arrow / swipe).
  useEffect(() => {
    if (!stripRef.current) return;
    const el = stripRef.current.querySelector(`[data-thumb-idx="${currentIndex}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [currentIndex]);

  // Touch / swipe
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (touchStart === null || touchEnd === null) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) goToNext();
    else if (distance < -minSwipeDistance) goToPrev();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!currentImage) return null;

  const reactionsEnabled = typeof onReact === 'function';
  const reactionButtons = reactionsEnabled ? getReactionButtons(reactionMode, t) : [];

  return (
    <div
      data-testid="image-lightbox"
      onClick={handleBackdropClick}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(245, 243, 240, 0.98)',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      {/* Close — smaller on mobile via CSS class */}
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        className="lightbox-close-btn"
        aria-label="Close"
      >
        ✕
      </button>

      {/* Image counter */}
      <div className="lightbox-counter">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Navigation arrows */}
      {images.length > 1 && (
        <>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); goToPrev(); }}
            className="lightbox-arrow-btn lightbox-arrow-prev"
            aria-label="Previous"
          >
            ←
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className="lightbox-arrow-btn lightbox-arrow-next"
            aria-label="Next"
          >
            →
          </button>
        </>
      )}

      {/* Main image container */}
      <div style={{
        maxWidth: '92vw',
        maxHeight: reactionsEnabled ? '64vh' : '70vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <img
          data-testid="lightbox-image"
          src={currentImage.image_full || currentImage.image_data}
          alt=""
          style={{
            maxWidth: '100%',
            maxHeight: reactionsEnabled ? '60vh' : '70vh',
            objectFit: 'contain',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            backgroundColor: 'var(--kahla-white)',
          }}
        />

        {/* Image info (section + comment). Reaction badge removed —
            the inline toolbar replaces it. */}
        <div style={{ marginTop: '16px', textAlign: 'center', maxWidth: '600px' }}>
          {currentImage.section_label && (
            <span style={{
              display: 'block',
              fontSize: '11px',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--kahla-taupe)',
              opacity: 0.95,
              marginBottom: '10px',
              fontWeight: 400,
            }}>
              {currentImage.section_label}
            </span>
          )}
          {currentImage.comment && (
            <p style={{
              marginTop: '8px',
              fontSize: '14px',
              fontStyle: 'italic',
              color: 'var(--kahla-text)',
              lineHeight: '1.6',
              fontFamily: '"Cormorant Garamond", serif',
              whiteSpace: 'pre-wrap',
            }}>
              "{currentImage.comment}"
            </p>
          )}
        </div>
      </div>

      {/* Inline reaction toolbar — only when parent supplied a handler.
          Sits below the image, never overlaps. */}
      {reactionsEnabled && (
        <div
          data-testid="lightbox-reactions"
          onClick={(e) => e.stopPropagation()}
          className="lightbox-reactions"
        >
          {reactionButtons.map((btn) => {
            const isActive = activeReaction === btn.value;
            const slug = btn.value === 'not_me' ? 'not-me' : btn.value;
            const showAsText = !btn.icon; // simple / decision modes
            return (
              <button
                key={btn.value}
                data-testid={`lightbox-react-${btn.value}`}
                aria-label={btn.label}
                aria-pressed={isActive}
                title={btn.label}
                onClick={(e) => {
                  e.stopPropagation();
                  // Toggle off if the same reaction is tapped twice — matches
                  // the grid-view toggling behavior.
                  onReact(currentImage, isActive ? null : btn.value);
                }}
                className={`reaction-btn ${slug} ${isActive ? 'active' : ''} ${showAsText ? 'is-text' : ''}`}
                style={showAsText ? {
                  // Calm pill style for text-only reaction modes — mirrors
                  // ClientView grid exactly so the lightbox feels native.
                  width: 'auto',
                  height: 'auto',
                  borderRadius: 0,
                  padding: '6px 14px',
                  fontFamily: 'inherit',
                  fontSize: '11px',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: isActive ? 'var(--kahla-charcoal)' : 'var(--kahla-text-light)',
                  background: isActive ? 'var(--kahla-cream-light)' : 'transparent',
                  border: `1px solid ${isActive ? 'var(--kahla-charcoal)' : 'var(--kahla-border)'}`,
                } : undefined}
              >
                {btn.icon || btn.shortLabel}
              </button>
            );
          })}
        </div>
      )}

      {/* Thumbnail strip — smoother + smaller, with snap-to-active. */}
      {images.length > 1 && (
        <div
          ref={stripRef}
          className="lightbox-thumbstrip"
          data-testid="lightbox-thumbstrip"
        >
          {images.map((img, idx) => (
            <button
              key={img.id}
              data-thumb-idx={idx}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
              className={`lightbox-thumb ${currentIndex === idx ? 'is-active' : ''}`}
              aria-label={`Image ${idx + 1}`}
            >
              <img
                src={img.image_thumb || img.image_data}
                alt=""
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}

      {/* Keyboard hint — desktop only (hidden via CSS on touch / small screens) */}
      <div className="lightbox-kbd-hint">
        {t.lightbox_kbd_hint || 'Use arrow keys to navigate · ESC to close'}
      </div>
    </div>
  );
};

export default ImageLightbox;
